import { useState } from 'react';

import {
  Button,
  Checkbox,
  Combobox,
  Group,
  Input,
  InputBase,
  Popover,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

export interface ChoicePickerOption {
  value: string;
  label: string;
}

/**
 * A multi-select that stays one line tall: a button reading "Any", the one
 * chosen label, or "N selected", opening a checkbox list (searchable when
 * asked). Built for dense editable tables, where a pill-based multi-select
 * wraps its row the moment anything is chosen.
 */
export function ChoicePicker({
  label,
  options,
  value,
  onChange,
  emptyLabel = 'Any',
  searchable = false,
  width = 280,
}: Readonly<{
  /** Accessible name of the control (also the search placeholder). */
  label: string;
  options: ChoicePickerOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** What the button reads while nothing is chosen. */
  emptyLabel?: string;
  searchable?: boolean;
  /** Dropdown width. */
  width?: number;
}>) {
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');

  const selected = options.filter((option) => value.includes(option.value));
  const summary =
    selected.length === 0
      ? emptyLabel
      : selected.length === 1
        ? selected[0].label
        : `${selected.length} selected`;
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? options.filter((option) => option.label.toLowerCase().includes(needle))
    : options;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      width={width}
      shadow="md"
      trapFocus
    >
      <Popover.Target>
        <Tooltip
          label={selected.map((option) => option.label).join(', ')}
          disabled={selected.length < 2}
          multiline
          maw={320}
        >
          {/* An input-styled button (Mantine's select-trigger idiom) so it
              matches the Selects beside it — typography, height, border,
              placeholder colour — instead of the theme's button styling. */}
          <InputBase
            component="button"
            type="button"
            pointer
            rightSection={<Combobox.Chevron />}
            rightSectionPointerEvents="none"
            onClick={() => setOpened((o) => !o)}
            aria-label={label}
            aria-haspopup="listbox"
            aria-expanded={opened}
            styles={{ input: { textAlign: 'left' } }}
            data-testid={`choice-picker-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {selected.length ? (
              <span
                style={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {summary}
              </span>
            ) : (
              <Input.Placeholder>{emptyLabel}</Input.Placeholder>
            )}
          </InputBase>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          {searchable ? (
            <TextInput
              size="xs"
              placeholder={`Search ${label.toLowerCase()}`}
              leftSection={<IconSearch size={14} />}
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              autoFocus
            />
          ) : null}
          <ScrollArea.Autosize mah={260} type="auto">
            <Checkbox.Group value={value} onChange={onChange}>
              <Stack gap={6} py={2}>
                {visible.map((option) => (
                  <Checkbox
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    size="xs"
                  />
                ))}
                {visible.length === 0 ? (
                  <Text size="xs" c="dimmed">
                    No matches
                  </Text>
                ) : null}
              </Stack>
            </Checkbox.Group>
          </ScrollArea.Autosize>
          <Group justify="space-between">
            <Button
              size="compact-xs"
              variant="subtle"
              disabled={value.length === 0}
              onClick={() => onChange([])}
            >
              Clear
            </Button>
            <Button size="compact-xs" variant="light" onClick={() => setOpened(false)}>
              Done
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
